import type {
  Configuration,
  CreateOrganizationIdpGroupRequest,
  CreateOrganizationIdpRequest,
  UpdateOrganizationIdpGroupRequest,
  UpdateOrganizationIdpRequest,
} from '@repo/api-gw-sdk';
import { OrganizationsApi } from '@repo/api-gw-sdk';

import type { HttpClient } from './httpClient';

const baseUrl = '/organization';

// The gateway reads nothing out of a page token but the index after the pipe.
// Deriving it from the index rather than a fresh uuid keeps the token stable
// across renders, which is what stops SWR re-keying and refetching on each one.
const idpGroupsPageToken = (pageIndex: number) => btoa(`page|${pageIndex}`);

export const organizations = (
  httpClient: HttpClient,
  config: Configuration
) => {
  const organizationsApi = new OrganizationsApi(config);

  return {
    accounts: {
      useList: () =>
        httpClient.execute(
          organizationsApi.listOrganizationAccounts.bind(organizationsApi),
          baseUrl,
          '/accounts/list'
        ),
    },
    accessibleAccounts: {
      useList: () =>
        httpClient.execute(
          organizationsApi.listAccessibleAccounts.bind(organizationsApi),
          baseUrl,
          '/accessible-accounts/list'
        ),
    },
    idps: {
      useList: (enabled = true) =>
        httpClient.execute(
          organizationsApi.listOrganizationIdps.bind(organizationsApi),
          enabled ? baseUrl : null,
          '/idps/list'
        ),
      create: (idp: CreateOrganizationIdpRequest) =>
        organizationsApi.createOrganizationIdp(idp),
      update: (idpId: string, idp: UpdateOrganizationIdpRequest) =>
        organizationsApi.updateOrganizationIdp(idpId, idp),
      delete: (idpId: string) => organizationsApi.deleteOrganizationIdp(idpId),
      useServiceProviderDetails: () =>
        httpClient.execute(
          organizationsApi.getOrganizationServiceProviderDetails.bind(
            organizationsApi
          ),
          baseUrl,
          '/idps/service-provider-details'
        ),
    },
    idpGroups: {
      useList: (pageIndex: number, pageSize: number, enabled = true) =>
        httpClient.execute(
          organizationsApi.listOrganizationIdpGroups.bind(organizationsApi),
          enabled ? baseUrl : null,
          '/idp-groups/list',
          pageIndex > 0 ? idpGroupsPageToken(pageIndex) : undefined,
          pageSize
        ),
      create: (input: CreateOrganizationIdpGroupRequest) =>
        organizationsApi.createOrganizationIdpGroup(input),
      update: (groupId: string, input: UpdateOrganizationIdpGroupRequest) =>
        organizationsApi.updateOrganizationIdpGroup(groupId, input),
      delete: (groupId: string) =>
        organizationsApi.deleteOrganizationIdpGroup(groupId),
    },
    mappableRoles: {
      // keepPreviousData is off so an account switch never shows the previous
      // account's catalog — a stale custom-role pick would 400 on save.
      useList: (accountId?: string, enabled = true) =>
        httpClient.executeWithConfig(
          organizationsApi.listMappableRoles.bind(organizationsApi),
          enabled ? baseUrl : null,
          '/mappable-roles/list',
          { keepPreviousData: false },
          { accountId }
        ),
    },
  };
};
